import { AuthUser } from '../@types/authentication';
import { AccessRule, Role } from './rules';

export type AccessResult = {
  canView: boolean;
  canEdit: boolean;
  requiredEditRole?: Role;
  isBeta?: boolean;
};

export const matchesClaim = (required: string, actual: string): boolean => {
  if (required === actual) {
    return true;
  }

  const separatorIndex = required.lastIndexOf('.');
  if (separatorIndex === -1) {
    return false;
  }

  const resource = required.slice(0, separatorIndex);
  const requiredOps = required.slice(separatorIndex + 1);

  if (!actual.startsWith(`${resource}.`)) {
    return false;
  }

  const actualOps = actual.slice(resource.length + 1);

  if (requiredOps === '*' || actualOps === '*') {
    return true;
  }

  return [...requiredOps].every(
    (op) =>
      actualOps.includes(op) ||
      (op === 'r' &&
        (actualOps.includes('c') || actualOps.includes('u') || actualOps.includes('d')))
  );
};

export const isAccessible = (claims: string[] | undefined, currentClaims: string[]): boolean => {
  if (claims === undefined) {
    return true;
  }

  return (
    currentClaims === undefined ||
    claims.some((required) => currentClaims.some((actual) => matchesClaim(required, actual)))
  );
};

export const getAccess = ({
  user,
  rule,
  currentClaims
}: {
  user: AuthUser;
  rule: AccessRule | undefined;
  currentClaims?: string[];
}): AccessResult => {
  if (!rule) return { canView: true, canEdit: true };

  if ('claims' in rule) {
    const hasAccess = isAccessible(rule.claims, currentClaims ?? []);
    return { canView: hasAccess, canEdit: hasAccess, isBeta: rule.isBeta };
  }

  let currentRoleIndex = Object.values(Role).indexOf(user?.role);
  if (currentRoleIndex === -1) currentRoleIndex = 999;

  const canView = currentRoleIndex <= Object.values(Role).indexOf(rule.viewRole);
  const requiredEditRole = rule.editRole;
  const canEdit =
    requiredEditRole === undefined ||
    currentRoleIndex <= Object.values(Role).indexOf(requiredEditRole);

  return { canView, canEdit, requiredEditRole };
};
